import { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home, Copy, Check } from 'lucide-react';
import { createErrorReport } from '@/types/errors';
import { reportBoundaryError } from '@/services/errorReporting';

interface Props {
  children: ReactNode;
  fallback?: ReactNode | ((props: FallbackProps) => ReactNode);
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  onReset?: () => void;
  maxRetries?: number;
  resetKeys?: Array<string | number>;
  boundaryName?: string;
}

export interface FallbackProps {
  error: Error | null;
  errorInfo: ErrorInfo | null;
  onRetry: () => void;
  onGoHome: () => void;
  retryCount: number;
  maxRetries: number;
  isRetrying: boolean;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  errorReportCopied: boolean;
  isRetrying: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  private resetTimeout: NodeJS.Timeout | null = null;
  private retryCountRef = 0;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorReportCopied: false,
      isRetrying: false,
    };
  }

  componentDidUpdate(prevProps: Props) {
    // Reset error state if resetKeys change
    if (this.props.resetKeys && prevProps.resetKeys) {
      const keysChanged = this.props.resetKeys.some(
        (key, index) => key !== prevProps.resetKeys![index]
      );
      if (keysChanged && this.state.hasError) {
        this.handleReset();
      }
    }
  }

  componentWillUnmount() {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { maxRetries = 3, boundaryName = 'ErrorBoundary' } = this.props;

    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    // Increment ref synchronously so we always read the latest count
    this.retryCountRef += 1;
    const currentRetry = this.retryCountRef;

    // Update state with error info
    this.setState({
      errorInfo,
      retryCount: currentRetry,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Attempt automatic recovery for certain error types
    if (currentRetry < maxRetries && this.isRecoverableError(error)) {
      this.setState({ isRetrying: true });
      this.resetTimeout = setTimeout(() => {
        this.handleReset();
      }, 1000 * Math.pow(2, currentRetry)); // Exponential backoff
    }

    // Report error to centralized error reporting service
    this.reportError(error, errorInfo, boundaryName);
  }

  isRecoverableError(error: Error): boolean {
    // Check if error might be recoverable (e.g., network errors, race conditions)
    const recoverablePatterns = [
      'network',
      'fetch',
      'timeout',
      'loading chunk failed',
      'dynamically imported module',
      'failed to fetch',
      'load failed',
    ];

    const errorMessage = error.message.toLowerCase();
    return recoverablePatterns.some((pattern) => errorMessage.includes(pattern));
  }

  reportError(error: Error, errorInfo: ErrorInfo, boundaryName: string) {
    try {
      // Report to centralized error reporting service
      reportBoundaryError(error, errorInfo, boundaryName, {
        retryCount: this.state.retryCount,
        maxRetries: this.props.maxRetries || 3,
        url: window.location.href,
      });
    } catch (reportErr) {
      console.error('Failed to report boundary error:', reportErr);
    }

    try {
      // Also create local error report for legacy compatibility
      createErrorReport(
        error,
        errorInfo.componentStack || undefined,
        undefined // Could pass user ID here if available
      );
    } catch (reportErr) {
      console.error('Failed to create error report:', reportErr);
    }
  }

  handleReset = () => {
    if (this.resetTimeout) {
      clearTimeout(this.resetTimeout);
    }
    this.retryCountRef = 0;
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      errorReportCopied: false,
      isRetrying: false,
    });

    // Call onReset callback if provided
    this.props.onReset?.();
  };

  handleRetry = () => {
    this.setState(prev => ({
      retryCount: prev.retryCount + 1,
    }));
    this.handleReset();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReportError = async () => {
    const { error, errorInfo } = this.state;

    if (!error || !errorInfo) return;

    // Create comprehensive error report
    const errorReport = createErrorReport(
      error,
      errorInfo.componentStack || undefined,
      undefined // Could pass user ID from auth store
    );

    // Copy to clipboard
    try {
      await navigator.clipboard.writeText(JSON.stringify(errorReport, null, 2));
      this.setState({ errorReportCopied: true });
      setTimeout(() => {
        this.setState({ errorReportCopied: false });
      }, 3000);
    } catch (err) {
      console.error('Failed to copy error report:', err);
      // Fallback: create a downloadable file
      this.downloadErrorReport(errorReport);
    }
  };

  downloadErrorReport = (report: any) => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `error-report-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  render() {
    const { hasError, error, errorInfo, retryCount, errorReportCopied, isRetrying } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      // Use custom fallback if provided (supports both ReactNode and render prop)
      if (fallback) {
        if (typeof fallback === 'function') {
          return fallback({
            error,
            errorInfo,
            onRetry: this.handleRetry,
            onGoHome: this.handleGoHome,
            retryCount,
            maxRetries,
            isRetrying,
          });
        }
        return fallback;
      }

      const isRecoverable = error ? this.isRecoverableError(error) : false;
      const canRetry = retryCount < maxRetries;

      // Default error UI
      return (
        <div className="mm-workplace mm-workplace-bg min-h-screen flex items-center justify-center px-4 py-12 text-white">
          <div className="max-w-2xl w-full">
            <div className="mm-glass shadow-2xl rounded-3xl p-8">
              {/* Error Icon */}
              <div className="flex justify-center mb-6">
                <div className="w-16 h-16 bg-rose-500/15 border border-rose-500/30 rounded-2xl flex items-center justify-center">
                  <AlertTriangle className="w-8 h-8 text-rose-200" />
                </div>
              </div>

              {/* Error Title */}
              <h1 className="text-2xl font-bold text-white text-center mb-4">
                Oops! Something went wrong
              </h1>

              {/* Error Message */}
              <p className="text-white/70 text-center mb-6">
                We're sorry for the inconvenience. An unexpected error has occurred.
              </p>

              {/* Retry Status */}
              {(isRetrying || (isRecoverable && canRetry)) && (
                <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-2xl p-4">
                  <p className="text-sm text-amber-200 text-center">
                    <RefreshCw className="w-4 h-4 inline-block mr-2 animate-spin" />
                    Attempting automatic recovery... (Attempt {retryCount}/{maxRetries})
                  </p>
                </div>
              )}

              {/* Error Details (Development) */}
              {process.env.NODE_ENV === 'development' && error && (
                <div className="mb-6 bg-white/5 border border-white/10 rounded-2xl p-4">
                  <h2 className="text-sm font-semibold text-white/80 mb-2">
                    Error Details (Development Only)
                  </h2>
                  <div className="text-xs text-rose-200 font-mono mb-2">
                    {error.message}
                  </div>
                  {error.stack && (
                    <details className="text-xs text-white/70 font-mono">
                      <summary className="cursor-pointer text-white/80 font-semibold mb-1">
                        Stack Trace
                      </summary>
                      <pre className="whitespace-pre-wrap overflow-auto max-h-64 bg-black/40 p-2 rounded border border-white/10">
                        {error.stack}
                      </pre>
                    </details>
                  )}
                  {errorInfo && (
                    <details className="text-xs text-white/70 font-mono mt-2">
                      <summary className="cursor-pointer text-white/80 font-semibold mb-1">
                        Component Stack
                      </summary>
                      <pre className="whitespace-pre-wrap overflow-auto max-h-64 bg-black/40 p-2 rounded border border-white/10">
                        {errorInfo.componentStack}
                      </pre>
                    </details>
                  )}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <button
                  onClick={this.handleRetry}
                  className="mm-btn mm-btn-primary inline-flex items-center justify-center px-4 py-2 text-sm"
                  disabled={isRetrying || (isRecoverable && canRetry)}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRetrying ? 'animate-spin' : ''}`} />
                  {isRetrying ? 'Retrying...' : 'Try Again'}
                </button>
                <button
                  onClick={this.handleGoHome}
                  className="mm-btn mm-btn-secondary inline-flex items-center justify-center px-4 py-2 text-sm"
                >
                  <Home className="w-4 h-4 mr-2" />
                  Go to Home
                </button>
                <button
                  onClick={this.handleReportError}
                  className="mm-btn mm-btn-secondary inline-flex items-center justify-center px-4 py-2 text-sm"
                >
                  {errorReportCopied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copy Error Report
                    </>
                  )}
                </button>
              </div>

              {/* Help Text */}
              <p className="text-sm text-white/60 text-center mt-6">
                If this problem persists, please contact support with the error report.
              </p>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
